import type { NoteSection, NoteSectionWithDeterministicFields } from '../types';

/**
 * Reads a section's deterministic-section kind, if it has one.
 *
 * The kind is a registry key string, deliberately not a closed union. This
 * returns whatever the backend sent, including a key this build has never
 * heard of: the caller decides what to do with an unknown one, and every
 * caller's answer is "render it read-only", never "treat it as an ordinary
 * editable section". Keying on "has a kind at all" is what stops a
 * backend-first kind from re-arming the placeholder gate or handing a
 * provider an editor over a slot the server owns.
 */
export const getNoteSectionKind = (section: NoteSection): string | undefined => {
  const kind = (section as NoteSectionWithDeterministicFields).kind;
  return typeof kind === 'string' && kind.length > 0 ? kind : undefined;
};

/**
 * The deterministic render frozen into the section at the author's
 * attestation, if any.
 *
 * Distinct from `edited_text`, which is the provider's own word even when
 * empty. Once set, this is what the provider attested to and it outranks
 * both `edited_text` and the model's `text` on every display path — the same
 * precedence the server applies when it flattens a note.
 */
export const getNoteSectionFrozenText = (section: NoteSection): string | undefined => {
  const frozen = (section as NoteSectionWithDeterministicFields).frozen_text;
  return typeof frozen === 'string' ? frozen : undefined;
};

/**
 * Whether this section's body was written by a machine rather than by the
 * provider or the model.
 */
export const isDeterministicNoteSection = (section: NoteSection): boolean => {
  const source = (section as NoteSectionWithDeterministicFields).source;
  return typeof source === 'string' && source.length > 0 && source !== 'llm';
};
